'use strict'

const KMZ_ALTITUDE_CONTEXT_SCOPE_PRIORITY = {
  playback: {
    globalHeight: ['template', 'waylines'],
    heightMode: ['template', 'waylines'],
    executeHeightMode: ['waylines', 'template'],
    useGlobalHeight: ['waylines', 'template']
  },
  templateExport: {
    globalHeight: ['template', 'waylines'],
    heightMode: ['template', 'waylines'],
    executeHeightMode: ['template', 'waylines'],
    useGlobalHeight: ['template', 'waylines']
  },
  waylinesExport: {
    globalHeight: ['waylines', 'template'],
    heightMode: ['waylines', 'template'],
    executeHeightMode: ['waylines', 'template'],
    useGlobalHeight: ['waylines', 'template']
  }
}

const KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG = {
  globalHeight: {
    scopedTargetPath: 'Folder.wpml:globalHeight',
    sourcePaths: [
      'Folder.wpml:globalHeight',
      'Folder.globalHeight',
      'wpml:globalHeight',
      'globalHeight'
    ]
  },
  heightMode: {
    scopedTargetPath: 'Folder.wpml:waylineCoordinateSysParam.wpml:heightMode',
    sourcePaths: [
      'Folder.wpml:waylineCoordinateSysParam.wpml:heightMode',
      'Folder.waylineCoordinateSysParam.heightMode',
      'wpml:waylineCoordinateSysParam.wpml:heightMode',
      'wpml:waylineCoordinateSysParam.heightMode',
      'waylineCoordinateSysParam.wpml:heightMode',
      'waylineCoordinateSysParam.heightMode',
      'wpml:heightMode',
      'heightMode'
    ]
  },
  executeHeightMode: {
    scopedTargetPath: 'Folder.wpml:executeHeightMode',
    sourcePaths: [
      'Folder.wpml:executeHeightMode',
      'Folder.executeHeightMode',
      'wpml:executeHeightMode',
      'executeHeightMode'
    ]
  },
  useGlobalHeight: {
    scopedTargetPath: 'wpml:useGlobalHeight',
    sourcePaths: [
      'wpml:useGlobalHeight',
      'useGlobalHeight'
    ]
  }
}

const KMZ_ALTITUDE_CONTEXT_ROOT_CACHE_PATHS = Object.keys(KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG)
  .map(function (fieldName) {
    return KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName].sourcePaths
  })
  .flat()

const fnCloneValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedAltitudeSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeAltitudeSource = function (pointProps, folderProps) {
  const out = {}
  Object.keys(KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG).forEach((fieldName) => {
    const fieldConfig = KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName]
    fnAssignScopedAltitudeSource(out, fieldConfig.scopedTargetPath, pointProps, fieldConfig.sourcePaths)
    if (fieldName === 'useGlobalHeight') return
    fnAssignScopedAltitudeSource(out, fieldConfig.scopedTargetPath, folderProps, fieldConfig.sourcePaths)
  })
  return out
}

const fnPruneKmzAltitudeCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_ALTITUDE_CONTEXT_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    Object.keys(KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG).forEach((fieldName) => {
      fnDeletePath(safeProps, scopeId + '.' + KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName].scopedTargetPath)
    })
  })
}

const buildKmzScopedAltitudePropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeAltitudeSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeAltitudeSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedAltitudeProps = function (props, scopedAltitudeProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedAltitudeProps && typeof scopedAltitudeProps === 'object') ? scopedAltitudeProps : {}
  fnPruneKmzAltitudeCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    Object.keys(KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG).forEach((fieldName) => {
      const fieldConfig = KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName]
      fnAssignScopedAltitudeSource(
        safeProps,
        scopeId + '.' + fieldConfig.scopedTargetPath,
        scopeProps,
        fieldConfig.sourcePaths.concat([fieldConfig.scopedTargetPath])
      )
    })
  })
  return safeProps
}

const syncKmzPointAltitudeScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedAltitudePropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedAltitudeProps(point.props, scoped)
  return point
}

const syncKmzMissionAltitudeScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointAltitudeScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeAltitudeContextPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  const lower = normalized.toLowerCase()
  if (lower.indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const resolveKmzAltitudeContextFieldKey = function (path) {
  const normalized = fnNormalizeAltitudeContextPathForCheck(path)
  if (!normalized) return ''
  if (normalized.indexOf('useglobalheight') !== -1) return 'useGlobalHeight'
  if (normalized.indexOf('executeheightmode') !== -1) return 'executeHeightMode'
  if (normalized.indexOf('waylinecoordinatesysparam') !== -1 && normalized.indexOf('heightmode') !== -1) return 'heightMode'
  if (normalized === 'wpml:heightmode' || normalized === 'heightmode') return 'heightMode'
  if (normalized === 'folder.wpml:globalheight' || normalized === 'folder.globalheight' || normalized === 'wpml:globalheight' || normalized === 'globalheight') return 'globalHeight'
  return ''
}

const isKmzAltitudeContextFieldPath = function (path) {
  return !!resolveKmzAltitudeContextFieldKey(path)
}

const isKmzGlobalAltitudeContextFieldPath = function (path) {
  const fieldKey = resolveKmzAltitudeContextFieldKey(path)
  return fieldKey === 'globalHeight' || fieldKey === 'heightMode' || fieldKey === 'executeHeightMode'
}

const hasKmzAltitudeContextScopedProps = function (pointOrProps) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  return ['template', 'waylines'].some((scopeId) => {
    return Object.keys(KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG).some((fieldName) => {
      return fnGetByPath(props, scopeId + '.' + KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName].scopedTargetPath) !== undefined
    })
  })
}

const fnResolveAltitudeContextCandidateByUseCase = function (pointOrProps, useCase, fieldName, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const fieldConfig = KMZ_ALTITUDE_CONTEXT_FIELD_CONFIG[fieldName]
  const priorityByUseCase = KMZ_ALTITUDE_CONTEXT_SCOPE_PRIORITY[useCase] || KMZ_ALTITUDE_CONTEXT_SCOPE_PRIORITY.playback
  const scopePriority = priorityByUseCase[fieldName] || KMZ_ALTITUDE_CONTEXT_SCOPE_PRIORITY.playback[fieldName] || ['template', 'waylines']
  if (!fieldConfig) {
    return {
      value: fallbackValue,
      scopeId: '',
      fieldName: String(fieldName || ''),
      path: ''
    }
  }
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    for (let j = 0; j < fieldConfig.sourcePaths.length; j += 1) {
      const path = scopeId + '.' + fieldConfig.sourcePaths[j]
      const value = fnGetByPath(props, path)
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return {
        value: value,
        scopeId: scopeId,
        fieldName: fieldName,
        path: path
      }
    }
    const scopedTargetValue = fnGetByPath(props, scopeId + '.' + fieldConfig.scopedTargetPath)
    if (scopedTargetValue === undefined || scopedTargetValue === null) continue
    if (typeof scopedTargetValue === 'string' && !String(scopedTargetValue).trim()) continue
    return {
      value: scopedTargetValue,
      scopeId: scopeId,
      fieldName: fieldName,
      path: scopeId + '.' + fieldConfig.scopedTargetPath
    }
  }
  return {
    value: fallbackValue,
    scopeId: '',
    fieldName: fieldName,
    path: ''
  }
}

const fnResolveAltitudeContextValueByUseCase = function (pointOrProps, useCase, fieldName, fallbackValue) {
  return fnResolveAltitudeContextCandidateByUseCase(pointOrProps, useCase, fieldName, fallbackValue).value
}

const fnToFiniteNumber = function (value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && !String(value).trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const resolveKmzMissionAltitudeContext = function (pointOrProps, useCase, fallbackContext) {
  const safeFallback = (fallbackContext && typeof fallbackContext === 'object') ? fallbackContext : {}
  const fieldUseCase = String(useCase || 'playback').trim() || 'playback'
  const resolvedHeightMode = String(
    fnResolveAltitudeContextValueByUseCase(pointOrProps, fieldUseCase, 'heightMode', safeFallback.heightMode || '')
  ).trim()
  return {
    globalHeight: fnToFiniteNumber(
      fnResolveAltitudeContextValueByUseCase(pointOrProps, fieldUseCase, 'globalHeight', safeFallback.globalHeight)
    ),
    heightMode: resolvedHeightMode,
    executeHeightMode: String(
      fnResolveAltitudeContextValueByUseCase(
        pointOrProps,
        fieldUseCase,
        'executeHeightMode',
        safeFallback.executeHeightMode || resolvedHeightMode
      )
    ).trim(),
    takeoffEllipsoidHeight: fnToFiniteNumber(safeFallback.takeoffEllipsoidHeight)
  }
}

const resolveKmzPlaybackGlobalHeight = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'playback', 'globalHeight', fallbackValue)
}

const resolveKmzPlaybackHeightMode = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'playback', 'heightMode', fallbackValue)
}

const resolveKmzPlaybackExecuteHeightMode = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'playback', 'executeHeightMode', fallbackValue)
}

const resolveKmzPlaybackUseGlobalHeight = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'playback', 'useGlobalHeight', fallbackValue)
}

const resolveKmzTemplateExportGlobalHeight = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'templateExport', 'globalHeight', fallbackValue)
}

const resolveKmzTemplateExportHeightMode = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'templateExport', 'heightMode', fallbackValue)
}

const resolveKmzTemplateExportUseGlobalHeight = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'templateExport', 'useGlobalHeight', fallbackValue)
}

const resolveKmzWaylinesExportExecuteHeightMode = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'waylinesExport', 'executeHeightMode', fallbackValue)
}

const resolveKmzWaylinesExportUseGlobalHeight = function (pointOrProps, fallbackValue) {
  return fnResolveAltitudeContextValueByUseCase(pointOrProps, 'waylinesExport', 'useGlobalHeight', fallbackValue)
}

const applyKmzResolvedAltitudeContextDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim() || 'playback'
  const globalHeight = fnResolveAltitudeContextValueByUseCase(point, safeUseCase, 'globalHeight', undefined)
  const heightMode = fnResolveAltitudeContextValueByUseCase(point, safeUseCase, 'heightMode', undefined)
  const executeHeightMode = fnResolveAltitudeContextValueByUseCase(point, safeUseCase, 'executeHeightMode', undefined)
  const useGlobalHeight = fnResolveAltitudeContextValueByUseCase(point, safeUseCase, 'useGlobalHeight', undefined)
  if (globalHeight !== undefined) {
    targetMap['wpml:globalHeight'] = globalHeight
  }
  if (heightMode !== undefined) {
    targetMap['wpml:waylineCoordinateSysParam.wpml:heightMode'] = heightMode
  }
  if (executeHeightMode !== undefined) {
    targetMap['wpml:executeHeightMode'] = executeHeightMode
  }
  if (useGlobalHeight !== undefined) {
    targetMap['wpml:useGlobalHeight'] = useGlobalHeight
  }
  return targetMap
}

export {
  buildKmzScopedAltitudePropsFromSources,
  applyKmzScopedAltitudeProps,
  syncKmzPointAltitudeScopesFromXml,
  syncKmzMissionAltitudeScopesFromXml,
  resolveKmzAltitudeContextFieldKey,
  isKmzAltitudeContextFieldPath,
  isKmzGlobalAltitudeContextFieldPath,
  hasKmzAltitudeContextScopedProps,
  resolveKmzMissionAltitudeContext,
  resolveKmzPlaybackGlobalHeight,
  resolveKmzPlaybackHeightMode,
  resolveKmzPlaybackExecuteHeightMode,
  resolveKmzPlaybackUseGlobalHeight,
  resolveKmzTemplateExportGlobalHeight,
  resolveKmzTemplateExportHeightMode,
  resolveKmzTemplateExportUseGlobalHeight,
  resolveKmzWaylinesExportExecuteHeightMode,
  resolveKmzWaylinesExportUseGlobalHeight,
  applyKmzResolvedAltitudeContextDisplayValuesToMap
}
